import {useCallback, useEffect, useState} from "react";
import {ContactView} from "../../../types/Contact";
import {DateTime, SelectableTableHeader, TableWithSelect, TextInputWithReset} from "zavadil-react-common";
import {ObjectUtil, PagingRequest} from "zavadil-ts-common";
import {useRestClient} from "../../../client/SpmRestClient";
import {useAlerts} from "../../../utils/UserAlerts";
import {Accordion, AccordionBody, AccordionHeader, AccordionItem, Form, Spinner} from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import SelectRegion from "../../../components/SelectRegion";
import SelectContactType from "../../../components/SelectContactType";
import SelectContactSource from "../../../components/SelectContactSource";
import SelectTargetGroups from "../../../components/SelectTargetGroups";
import {SpmPage} from "../../../types/SpmPage";
import ContactDetail from "./ContactDetail";

const HEADER: SelectableTableHeader<ContactView> = [
	{name: "name", label: "Název"},
	{name: 'regionName', label: 'Region', sort: "region.name"},
	{name: 'typeName', label: 'Druh', sort: "type.name"},
	{name: 'lastUpdate', label: 'Naposledy aktualizováno', renderer: (c) => <DateTime value={c.lastUpdate}/>},
	{name: 'notFoundCounter', label: 'Scraping selhal'}
];

const DEFAULT_PAGING: PagingRequest = {page: 0, size: 100, sorting: [{name: "name"}]};

export default function ContactsPage() {
	const restClient = useRestClient();
	const userAlerts = useAlerts();
	const [paging, setPaging] = useState<PagingRequest>(ObjectUtil.clone(DEFAULT_PAGING));
	const [regionId, setRegionId] = useState<number | null>();
	const [sourceId, setSourceId] = useState<string | null>();
	const [typeId, setTypeId] = useState<number | null>();
	const [targetGroups, setTargetGroups] = useState<Array<number>>([]);
	const [fulltext, setFulltext] = useState<string>('');
	const [page, setPage] = useState<SpmPage<ContactView>>();
	const [detail, setDetail] = useState<ContactView | null>(null);

	const fetchContacts = useCallback(
		() => {
			setPage(undefined);
			restClient.contacts.filtered(
				regionId,
				sourceId,
				typeId,
				targetGroups,
				fulltext,
				paging
			)
				.then(setPage)
				.catch((e) => userAlerts.err(e))
		},
		[paging, regionId, sourceId, typeId, targetGroups, fulltext, restClient, userAlerts]
	);

	useEffect(fetchContacts, [paging, regionId, sourceId, typeId, targetGroups, fulltext]);

	return (
		<div>
			<h1>Kontakty</h1>
			<Form onSubmit={fetchContacts}>
				<Row>
					<Col>
						<Form.Label>Text</Form.Label>
						<TextInputWithReset
							value={fulltext}
							onChange={
								(text) => {
									setPaging({...paging, page: 0});
									setFulltext(text);
								}
							}
						/>
					</Col>
				</Row>
				<Row className="mt-2">
					<Col>
						<Form.Label>Region</Form.Label>
						<SelectRegion
							regionId={regionId}
							onChange={
								(id) => {
									setPaging({...paging, page: 0});
									setRegionId(id);
								}
							}
						/>
					</Col>
					<Col>
						<Form.Label>Zdroj dat</Form.Label>
						<SelectContactSource
							sourceId={sourceId}
							onChange={
								(id) => {
									setPaging({...paging, page: 0});
									setSourceId(id);
								}
							}
						/>
					</Col>
					<Col>
						<Form.Label>Druh pomoci</Form.Label>
						<SelectContactType
							selectedContactTypeId={typeId}
							onChange={
								(id) => {
									setPaging({...paging, page: 0});
									setTypeId(id);
								}
							}
						/>
					</Col>
				</Row>
				<Row className="mt-2">
					<Col>
						<Accordion>
							<AccordionItem eventKey="">
								<AccordionHeader>
									Cílové skupiny
								</AccordionHeader>
								<AccordionBody>
									<div className="target-groups">
										<SelectTargetGroups
											selectedGroupIds={targetGroups}
											onChange={
												(ids) => {
													setPaging({...paging, page: 0});
													setTargetGroups(ids);
												}
											}
										/>
									</div>
								</AccordionBody>
							</AccordionItem>
						</Accordion>
					</Col>
				</Row>
			</Form>

			{
				page ? <TableWithSelect
					header={HEADER}
					hover={true}
					onPagingChanged={setPaging}
					paging={paging}
					onClick={setDetail}
					showSelect={false}
					items={page?.content}
					totalItems={page?.totalElements || 0}

				/> : <Spinner/>
			}
			{detail && <ContactDetail contact={detail} onClosed={() => setDetail(null)}/>}
		</div>
	);
}

